import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBGIFEmptyResults } from './empty';
import { MBGIFError } from './error';
import { MBGIFListRow } from './row';
import styles from './search.module.css';

interface GIFSearchComponentProps {
  isLoading: boolean;
  isError: boolean;
  gifs: MBGIF[];
  onSearch: (query: string) => Promise<void>;
  onSelect: (gif: MBGIF) => void;
  onLoadMore: () => void;
}

const LOAD_MORE_THRESHOLD = 200;
const SEARCH_DEBOUNCE_TIME = 200;

export function GIFSearchComponent(props: GIFSearchComponentProps) {
  const { onSearch, onLoadMore } = props;
  const scrollRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(search);
    }, SEARCH_DEBOUNCE_TIME);
    return () => clearTimeout(timer);
  }, [search, onSearch]);

  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const remainingScrollHeight = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (remainingScrollHeight < LOAD_MORE_THRESHOLD) {
        onLoadMore();
      }
    },
    [onLoadMore],
  );

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [search]);

  return (
    <div className={styles.gifSearch}>
      <div className={styles.searchBar}>
        <MBSearchBar autoFocus={true} value={search} onChangeText={setSearch} />
      </div>
      <GifSearchContent {...props} scrollRef={scrollRef} onScroll={onScroll} />
    </div>
  );
}

interface GifSearchContentProps extends GIFSearchComponentProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

function GifSearchContent({
  isLoading,
  isError,
  gifs,
  scrollRef,
  onScroll,
  onSelect,
}: GifSearchContentProps) {
  if (isLoading) {
    return <LoadingIndicator fill />;
  }
  if (isError) {
    return <MBGIFError />;
  }
  if (isEmpty(gifs)) {
    return <MBGIFEmptyResults />;
  }

  return (
    <div ref={scrollRef} className={styles.content} onScroll={onScroll}>
      <div className={styles.grid}>
        {gifs.map(gif => (
          <MBGIFListRow
            key={gif.id}
            source={{ uri: gif.previewUrl }}
            onPress={() => onSelect(gif)}
          />
        ))}
      </div>
    </div>
  );
}
